import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container, Typography, Box, TextField, Button, Grid,
  FormControlLabel, Checkbox, Alert, CircularProgress,
  Select, MenuItem, FormControl, InputLabel,
} from '@mui/material';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import HomeWorkIcon from '@mui/icons-material/HomeWork';
import ConstructionIcon from '@mui/icons-material/Construction';
import WarehouseIcon from '@mui/icons-material/Warehouse';
import AgricultureIcon from '@mui/icons-material/Agriculture';
import Inventory2Icon from '@mui/icons-material/Inventory2';
import logisticsService from '../../services/logistics.service';
import MapboxAddressInput from '../../components/MapboxAddressInput';
import MiniMap from '../../components/MiniMap';

/* ── Design tokens ─────────────────────────────────────────── */
const T = {
  purple: '#552583', purpleDark: '#3d1a61', gold: '#FDB927',
  bg: '#E4F0F6', white: '#ffffff', ink: '#1a1a1a', sub: '#4a4a4a', muted: '#7a7a7a',
  border: 'rgba(85,37,131,0.08)',
  radius: { sm: '8px', md: '10px', btn: '12px', card: '20px' },
  transition: '0.15s ease',
  shadow: '0 1px 4px rgba(0,0,0,0.06)',
};

const sectionBox = {
  p: 2.5, bgcolor: T.white, borderRadius: T.radius.card,
  border: `1px solid ${T.border}`, boxShadow: T.shadow, mb: 2,
};

const sectionTitle = {
  fontWeight: 600, fontSize: '0.7rem', color: T.muted,
  textTransform: 'uppercase', letterSpacing: '0.1em', mb: 1.5,
};

const cargoTypes = [
  { value: 'demenagement', label: 'Déménagement', IconComp: HomeWorkIcon },
  { value: 'materiaux', label: 'Matériaux BTP', IconComp: ConstructionIcon },
  { value: 'marchandises', label: 'Marchandises', IconComp: WarehouseIcon },
  { value: 'agricole', label: 'Produits agricoles', IconComp: AgricultureIcon },
  { value: 'divers', label: 'Divers / Autre', IconComp: Inventory2Icon },
];

const truckTypes = [
  { value: 'pickup', label: 'Pick-up / Plateau', desc: 'Jusqu\'à 1.5T', tonnage: '1.5' },
  { value: 'fourgon', label: 'Fourgon', desc: 'Jusqu\'à 3.5T', tonnage: '3.5' },
  { value: 'camion_moyen', label: 'Camion moyen', desc: '3.5T - 7.5T', tonnage: '7.5' },
  { value: 'poids_lourd', label: 'Poids lourd', desc: '7.5T - 19T', tonnage: '19' },
  { value: 'semi', label: 'Semi-remorque', desc: '19T+', tonnage: '25' },
];

const LogisticsCreate = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    pickup_contact_name: '', pickup_contact_phone: '',
    pickup_address: '', pickup_latitude: null, pickup_longitude: null,
    delivery_contact_name: '', delivery_contact_phone: '',
    delivery_address: '', delivery_latitude: null, delivery_longitude: null,
    package_type: 'marchandises', weight_kg: '', dimensions: '', description: '',
    is_fragile: false, is_urgent: false, delivery_type: 'standard',
    truck_type: 'fourgon', loading_help: false, unloading_help: false,
    preferred_date: '',
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData({ ...formData, [name]: type === 'checkbox' ? checked : value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault(); setError(''); setLoading(true);
    try {
      await logisticsService.createOrder(formData);
      setSuccess(true);
      setTimeout(() => navigate('/logistics/orders'), 2000);
    } catch (err) {
      setError(err.response?.data?.message || 'Erreur lors de la création');
    } finally { setLoading(false); }
  };

  const canShowMap = formData.pickup_latitude && formData.delivery_latitude;

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: T.bg, pb: 6 }}>
      <Container maxWidth="md" sx={{ pt: 4 }}>

        {/* Header */}
        <Box sx={{ mb: 3.5 }}>
          <Typography sx={{ fontWeight: 800, color: T.ink, fontSize: '1.5rem', letterSpacing: '-0.01em' }}>
            Transport de matériel
          </Typography>
          <Typography sx={{ color: T.muted, mt: 0.3, fontSize: '0.85rem' }}>
            Faites transporter vos marchandises et matériaux par camion à Mayotte
          </Typography>
        </Box>

        {error && <Alert severity="error" sx={{ mb: 2, borderRadius: T.radius.sm }}>{error}</Alert>}
        {success && <Alert severity="success" sx={{ mb: 2, borderRadius: T.radius.sm }}>Demande de transport créée avec succès !</Alert>}

        <form onSubmit={handleSubmit}>

          {/* Cargo type */}
          <Box sx={sectionBox}>
            <Typography sx={sectionTitle}>Type de chargement</Typography>
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              {cargoTypes.map((type) => {
                const selected = formData.package_type === type.value;
                return (
                  <Box
                    key={type.value}
                    onClick={() => setFormData({ ...formData, package_type: type.value })}
                    sx={{
                      flex: { xs: '1 1 calc(50% - 8px)', sm: '1 1 0' },
                      p: 1.5, borderRadius: T.radius.btn, cursor: 'pointer', textAlign: 'center',
                      border: `1px solid ${selected ? T.purple : T.border}`,
                      bgcolor: selected ? 'rgba(85,37,131,0.04)' : 'transparent',
                      transition: T.transition,
                      '&:hover': { borderColor: 'rgba(85,37,131,0.25)' },
                    }}
                  >
                    <type.IconComp sx={{ fontSize: '1.3rem', mb: 0.2, color: selected ? T.purple : T.muted }} />
                    <Typography sx={{ fontSize: '0.72rem', fontWeight: selected ? 600 : 500, color: selected ? T.purple : T.ink }}>
                      {type.label}
                    </Typography>
                  </Box>
                );
              })}
            </Box>
          </Box>

          {/* Truck type */}
          <Box sx={sectionBox}>
            <Typography sx={sectionTitle}>Type de camion</Typography>
            <Grid container spacing={1}>
              {truckTypes.map((type) => {
                const selected = formData.truck_type === type.value;
                return (
                  <Grid item xs={6} sm={4} key={type.value}>
                    <Box
                      onClick={() => setFormData({ ...formData, truck_type: type.value })}
                      sx={{
                        p: 1.5, borderRadius: T.radius.btn, cursor: 'pointer',
                        border: `1px solid ${selected ? T.purple : T.border}`,
                        bgcolor: selected ? 'rgba(85,37,131,0.04)' : 'transparent',
                        transition: T.transition,
                        '&:hover': { borderColor: 'rgba(85,37,131,0.25)' },
                      }}
                    >
                      <Typography sx={{ fontWeight: 600, fontSize: '0.85rem', color: selected ? T.purple : T.ink }}>{type.label}</Typography>
                      <Typography sx={{ fontSize: '0.68rem', color: T.muted }}>{type.desc}</Typography>
                    </Box>
                  </Grid>
                );
              })}
            </Grid>
          </Box>

          {/* Cargo details */}
          <Box sx={sectionBox}>
            <Typography sx={sectionTitle}>Détails du chargement</Typography>
            <Grid container spacing={1.5}>
              <Grid item xs={12} sm={6}>
                <TextField fullWidth label="Poids estimé (kg)" name="weight_kg" type="number"
                  value={formData.weight_kg} onChange={handleChange} inputProps={{ step: 10 }} size="small"
                  placeholder="Ex: 500" />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField fullWidth label="Volume / Dimensions" name="dimensions"
                  value={formData.dimensions} onChange={handleChange}
                  placeholder="Ex: 2 palettes, 3m3, etc." size="small" />
              </Grid>
              <Grid item xs={12}>
                <TextField fullWidth label="Description du matériel à transporter" name="description"
                  value={formData.description} onChange={handleChange} multiline rows={2} required size="small"
                  placeholder="Décrivez le type de matériel, la quantité, les conditions particulières..." />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField fullWidth label="Date souhaitée" name="preferred_date" type="date"
                  value={formData.preferred_date} onChange={handleChange}
                  InputLabelProps={{ shrink: true }} size="small" />
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth size="small">
                  <InputLabel>Urgence</InputLabel>
                  <Select name="delivery_type" value={formData.delivery_type} onChange={handleChange} label="Urgence">
                    <MenuItem value="standard">Standard (3-5 jours)</MenuItem>
                    <MenuItem value="express">Express (24-48h)</MenuItem>
                    <MenuItem value="same_day">Urgent (même jour)</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12}>
                <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                  <FormControlLabel
                    control={<Checkbox name="is_fragile" checked={formData.is_fragile} onChange={handleChange} size="small"
                      sx={{ color: T.muted, '&.Mui-checked': { color: T.purple } }} />}
                    label={<Typography sx={{ fontSize: '0.82rem', color: T.sub }}>Matériel fragile</Typography>}
                  />
                  <FormControlLabel
                    control={<Checkbox name="loading_help" checked={formData.loading_help} onChange={handleChange} size="small"
                      sx={{ color: T.muted, '&.Mui-checked': { color: T.purple } }} />}
                    label={<Typography sx={{ fontSize: '0.82rem', color: T.sub }}>Aide au chargement</Typography>}
                  />
                  <FormControlLabel
                    control={<Checkbox name="unloading_help" checked={formData.unloading_help} onChange={handleChange} size="small"
                      sx={{ color: T.muted, '&.Mui-checked': { color: T.purple } }} />}
                    label={<Typography sx={{ fontSize: '0.82rem', color: T.sub }}>Aide au déchargement</Typography>}
                  />
                </Box>
              </Grid>
            </Grid>
          </Box>

          {/* Pickup */}
          <Box sx={sectionBox}>
            <Typography sx={sectionTitle}>Lieu de chargement</Typography>
            <Grid container spacing={1.5}>
              <Grid item xs={12} sm={6}>
                <TextField fullWidth label="Nom du responsable" name="pickup_contact_name"
                  value={formData.pickup_contact_name} onChange={handleChange} required size="small" />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField fullWidth label="Téléphone" name="pickup_contact_phone"
                  value={formData.pickup_contact_phone} onChange={handleChange} required size="small" />
              </Grid>
              <Grid item xs={12}>
                <MapboxAddressInput
                  label="Adresse de chargement" placeholder="Où récupérer le matériel ?"
                  value={formData.pickup_address}
                  onSelect={(result) => {
                    if (!result) { setFormData(p => ({ ...p, pickup_address: '', pickup_latitude: null, pickup_longitude: null })); return; }
                    setFormData(p => ({ ...p, pickup_address: result.fullAddress || result.place_name || result.address, pickup_latitude: result.latitude, pickup_longitude: result.longitude }));
                  }}
                  showGeolocate
                />
              </Grid>
            </Grid>
          </Box>

          {/* Delivery */}
          <Box sx={sectionBox}>
            <Typography sx={sectionTitle}>Lieu de livraison</Typography>
            <Grid container spacing={1.5}>
              <Grid item xs={12} sm={6}>
                <TextField fullWidth label="Nom du destinataire" name="delivery_contact_name"
                  value={formData.delivery_contact_name} onChange={handleChange} required size="small" />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField fullWidth label="Téléphone" name="delivery_contact_phone"
                  value={formData.delivery_contact_phone} onChange={handleChange} required size="small" />
              </Grid>
              <Grid item xs={12}>
                <MapboxAddressInput
                  label="Adresse de livraison" placeholder="Où livrer le matériel ?"
                  value={formData.delivery_address}
                  onSelect={(result) => {
                    if (!result) { setFormData(p => ({ ...p, delivery_address: '', delivery_latitude: null, delivery_longitude: null })); return; }
                    setFormData(p => ({ ...p, delivery_address: result.fullAddress || result.place_name || result.address, delivery_latitude: result.latitude, delivery_longitude: result.longitude }));
                  }}
                  showGeolocate={false}
                />
              </Grid>
            </Grid>
          </Box>

          {/* Map preview */}
          {canShowMap && (
            <Box sx={sectionBox}>
              <Typography sx={sectionTitle}>Aperçu du trajet</Typography>
              <Box sx={{ borderRadius: T.radius.btn, overflow: 'hidden' }}>
                <MiniMap
                  pickupLat={formData.pickup_latitude} pickupLng={formData.pickup_longitude}
                  dropoffLat={formData.delivery_latitude} dropoffLng={formData.delivery_longitude}
                  height={200}
                  onPickupDragEnd={({ latitude, longitude }) => setFormData(p => ({ ...p, pickup_latitude: latitude, pickup_longitude: longitude }))}
                  onDropoffDragEnd={({ latitude, longitude }) => setFormData(p => ({ ...p, delivery_latitude: latitude, delivery_longitude: longitude }))}
                />
              </Box>
            </Box>
          )}

          {/* Submit */}
          <Button
            type="submit" fullWidth variant="contained" disabled={loading}
            startIcon={!loading && <LocalShippingIcon sx={{ fontSize: '1.1rem !important' }} />}
            sx={{
              py: 1.5, borderRadius: T.radius.btn, fontWeight: 700, fontSize: '0.92rem', textTransform: 'none',
              bgcolor: T.purple, color: 'white', boxShadow: 'none', transition: T.transition,
              '&:hover': { bgcolor: T.purpleDark, boxShadow: 'none' },
              '&:disabled': { bgcolor: 'rgba(85,37,131,0.12)', color: T.muted, boxShadow: 'none' },
            }}
          >
            {loading ? <CircularProgress size={22} sx={{ color: 'white' }} /> : 'Demander un transport'}
          </Button>
        </form>
      </Container>
    </Box>
  );
};

export default LogisticsCreate;
